/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { usePrompts } from '@/hooks/usePrompts';
import { useTraceId } from '@/hooks/useTraceID';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { CustomButton, SearchInput } from '@/components/common';
import { FACT_NAME_LABEL_MAP } from '@/components/display-page/fact-name-label-map';

export const TraceIdSearch = ({
  setIsOverlayActive,
  isPromptSearch,
  initialValue = '',
  setTraceId,
  selectedFact,
  activeTab,
}: {
  setIsOverlayActive: (value: boolean) => void;
  isPromptSearch?: boolean;
  initialValue?: string;
  setTraceId?: Dispatch<SetStateAction<string | null>>;
  selectedFact?: string;
  activeTab?: string;
}) => {
  const searchParams = useSearchParams();

  const { fetchTraceByID, fetchFactMeasures, clearTraceIDResults } =
    useTraceId();
  const prompts = usePrompts();
  const fetchPromptsByTraceId =
    isPromptSearch && prompts?.fetchPromptsByTraceId;
  const clearPrompts = isPromptSearch && prompts?.clearPrompts;
  const { messages } = useLanguage();
  const promptFilterIntl = messages?.PromptFilters;
  const currentPath = usePathname();
  const isComponentPage = currentPath.includes('/components');

  const FormSchema = z.object({
    traceId: z.string(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      traceId: initialValue,
    },
  });

  const lastFetchedValue = useRef<string>('');

  useEffect(() => {
    // skip if not on search tab
    if (
      activeTab !== 'search' ||
      (!isComponentPage && (!selectedFact || selectedFact === undefined))
    )
      return;

    if (initialValue) {
      form.reset({ traceId: initialValue });
      // URL factName as source of truth
      const urlFactName = searchParams.get('factName');
      const resolvedFact = urlFactName || selectedFact || 'traces';

      const fetchKey = `${initialValue}:${resolvedFact}`;

      // skip if already fetched
      if (lastFetchedValue.current === fetchKey) return;
      lastFetchedValue.current = fetchKey;
      if (isComponentPage) {
        fetchTraceByID(initialValue, 'traces');
      } else {
        fetchTraceByID(initialValue, resolvedFact);
        fetchFactMeasures(resolvedFact, initialValue);
      }
    }
  }, [initialValue, activeTab, selectedFact, searchParams]);
  const { formState, reset, watch } = form;
  const { isDirty } = formState;

  useEffect(() => {
    setIsOverlayActive(isDirty);
  }, [isDirty]);

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    reset(data);
    setTraceId?.(data?.traceId);
    if (isPromptSearch && fetchPromptsByTraceId) {
      await fetchPromptsByTraceId(data.traceId);
    } else {
      if (selectedFact) {
        // Mark as fetched so the useEffect triggered by initialValue change doesn't duplicate the call
        lastFetchedValue.current = `${data.traceId}:${selectedFact}`;
        await fetchTraceByID(data.traceId, selectedFact);
        await fetchFactMeasures(selectedFact, data.traceId);
      } else if (isComponentPage) {
        lastFetchedValue.current = `${data.traceId}:traces`;
        await fetchTraceByID(data.traceId, 'traces');
      }
    }
  };

  const traceIdValue = watch('traceId');

  const handleClear = () => {
    reset({ traceId: '' });
    if (isPromptSearch && clearPrompts) {
      clearPrompts();
    } else {
      clearTraceIDResults();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex">
          <FormField
            control={form.control}
            name="traceId"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormControl>
                  <SearchInput
                    onClear={handleClear} // Updated clear handler
                    placeholder={
                      selectedFact
                        ? FACT_NAME_LABEL_MAP?.[selectedFact]?.idLabel ||
                          promptFilterIntl?.search_id_placeholder
                        : promptFilterIntl?.search_id_placeholder
                    }
                    className="h-7 rounded-none rounded-l-sm"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <CustomButton
            type="submit"
            className="h-7 rounded-r-sm text-form-input-text font-medium text-body-secondary"
            disabled={traceIdValue.length < 3}
          >
            {promptFilterIntl?.go}
          </CustomButton>
        </div>
      </form>
    </Form>
  );
};
